import { Field, Input } from 'components';
import QuestionOrganism from 'questionFlow/QuestionOrganism';
import { useCallback, useMemo } from 'react';
import { TFunction, useTranslation, Trans } from 'react-i18next';
import { ValidationRule } from '../types';
import {
  DefaultIncomeQuestionComponentProps,
  StepNamesDefaultIncome,
} from 'questionFlow/flows/defaultIncome/types';
import { useAnalyticsErrors, useAnalyticsValues } from 'monitoring/analyticsHooks';
import { GENERIC_EMAIL_DOMAIN, GENERIC_EMAIL_PREFIX } from '../validations';

export enum CompanyReferenceFieldsEnum {
  referenceName = 'referenceName',
  referenceEmail = 'referenceEmail',
}

export type CompanyReferenceValues = {
  [key in CompanyReferenceFieldsEnum]: string;
};

const cmsLocation = '/questions/income/companyReference';

const useContent = (t: TFunction<'translation'>) => ({
  title: t(`${cmsLocation}.title`),
  subtitle: t(`${cmsLocation}.subtitle`),

  referenceName: {
    label: t(`${cmsLocation}.referenceName.label`),
    placeholder: t(`${cmsLocation}.referenceName.placeholder`),
  },
  referenceEmail: {
    label: t(`${cmsLocation}.referenceEmail.label`),
    placeholder: t(`${cmsLocation}.referenceEmail.placeholder`),
  },
});

interface InputEventTarget {
  name: CompanyReferenceFieldsEnum;
  value: string;
}

export const useErrors = () =>
  useAnalyticsErrors<CompanyReferenceValues>({
    [CompanyReferenceFieldsEnum.referenceName]: '',
    [CompanyReferenceFieldsEnum.referenceEmail]: '',
  });

export const useWarnings = () =>
  useAnalyticsErrors<CompanyReferenceValues>({
    [CompanyReferenceFieldsEnum.referenceName]: '',
    [CompanyReferenceFieldsEnum.referenceEmail]: '',
  });

const validate = (v: string, rules: ValidationRule[]) => rules.find(({ rule }) => !rule(v));

const stepName = StepNamesDefaultIncome.companyReference;

const CompanyReference = ({
  data,
  onSubmit,
  values: initialValues,
  validationRules,
  ...props
}: DefaultIncomeQuestionComponentProps) => {
  const { t } = useTranslation();
  const content = useContent(t);
  const [values, setValues] = useAnalyticsValues<CompanyReferenceValues>({
    [CompanyReferenceFieldsEnum.referenceName]:
      initialValues?.companyReference?.referenceName || '',
    [CompanyReferenceFieldsEnum.referenceEmail]:
      initialValues?.companyReference?.referenceEmail || '',
  });
  const [error, setError] = useErrors();
  const [warning, setWarning] = useWarnings();

  const PERSONAL_EMAIL_RULE = useMemo(
    () =>
      Object.freeze<ValidationRule>({
        name: 'personal-email',
        rule: v => !v || v.toLowerCase() !== data.email.toLowerCase(),
        error: `${cmsLocation}.validation.personalEmailMatch`,
      }),
    [data.email],
  );

  const GENERIC_EMAIL_DOMAIN_WARNING = useMemo(
    () =>
      Object.freeze<ValidationRule>({
        name: 'generic-email-domain-warning',
        rule: (value: string) => {
          if (!value) return true; // Don't warn on empty values
          return GENERIC_EMAIL_DOMAIN.rule(value);
        },
        error: `${cmsLocation}.validation.genericDomainWarning`,
      }),
    [],
  );

  const GENERIC_EMAIL_PREFIX_WARNING = useMemo(
    () =>
      Object.freeze<ValidationRule>({
        name: 'generic-email-prefix-warning',
        rule: (value: string) => {
          if (!value) return true; // Don't warn on empty values
          return GENERIC_EMAIL_PREFIX.rule(value);
        },
        error: `${cmsLocation}.validation.genericPrefixWarning`,
      }),
    [],
  );

  const getValidationResult = useCallback(
    (name: CompanyReferenceFieldsEnum, givenValue?: string) => {
      const actualValue = givenValue === undefined ? values[name] : givenValue;
      const result = validate(actualValue, validationRules[name]);

      if (!result && name === 'referenceEmail') {
        // Run an additional check that the email doesn't match the user's email address
        return validate(actualValue, [PERSONAL_EMAIL_RULE]);
      }

      return result;
    },
    [PERSONAL_EMAIL_RULE, validationRules, values],
  );

  const getWarningResult = useCallback(
    (name: CompanyReferenceFieldsEnum, givenValue?: string) => {
      const actualValue = givenValue === undefined ? values[name] : givenValue;

      if (name === 'referenceEmail') {
        // Check for generic email warning (only if there are no validation errors)
        const hasValidationError = getValidationResult(name, actualValue);
        if (!hasValidationError) {
          // Check for generic prefix first (more specific)
          const prefixResult = validate(actualValue, [GENERIC_EMAIL_PREFIX_WARNING]);
          if (prefixResult) {
            return prefixResult;
          }

          // Then check for generic domain
          const domainResult = validate(actualValue, [GENERIC_EMAIL_DOMAIN_WARNING]);
          if (domainResult) {
            return domainResult;
          }
        }
      }

      return undefined;
    },
    [getValidationResult, GENERIC_EMAIL_PREFIX_WARNING, GENERIC_EMAIL_DOMAIN_WARNING, values],
  );

  const handleSubmit = useCallback(() => {
    const errors = (Object.keys(values) as CompanyReferenceFieldsEnum[]).reduce((e, inputName) => {
      const result = getValidationResult(inputName);

      return { ...e, [inputName]: result ? t(result.error as any) : '' };
    }, error);

    if (Object.values(errors).filter(e => e !== '').length === 0) {
      onSubmit({ key: stepName, value: values });
    } else {
      setError(errors);
    }
  }, [error, getValidationResult, onSubmit, setError, t, values]);

  const validateAndSetError = useCallback(
    (name: CompanyReferenceFieldsEnum, newValue: string) => {
      const result = getValidationResult(name, newValue);
      setError({ ...error, [name]: result ? t(result.error as any) : '' });

      // Check for warnings (only if no validation errors)
      const warningResult = getWarningResult(name, newValue);
      setWarning({ ...warning, [name]: warningResult ? t(warningResult.error as any) : '' });
    },
    [error, warning, getValidationResult, getWarningResult, setError, setWarning, t],
  );

  const handleInputOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target as InputEventTarget;
      const trimmedValue = value.trim();

      if (
        error[name as CompanyReferenceFieldsEnum] ||
        warning[name as CompanyReferenceFieldsEnum]
      ) {
        validateAndSetError(name, trimmedValue);
      }

      // Always check for warnings on email field as user types
      if (name === 'referenceEmail' && trimmedValue) {
        const warningResult = getWarningResult(name as CompanyReferenceFieldsEnum, trimmedValue);
        setWarning({ ...warning, [name]: warningResult ? t(warningResult.error as any) : '' });
      }

      setValues({ ...values, [name]: trimmedValue });
    },
    [error, warning, setValues, validateAndSetError, values, getWarningResult, setWarning, t],
  );

  const handleInputOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = e.target as InputEventTarget;
      validateAndSetError(name, value.trim());
    },
    [validateAndSetError],
  );

  const subtitle = (
    <Trans
      i18nKey={`${cmsLocation}.subtitle`}
      components={{
        bold: <b />,
        br: <br />,
      }}
    />
  );

  return (
    <QuestionOrganism
      id={stepName}
      data-testid={stepName}
      onSubmit={handleSubmit}
      title={content.title}
      subtitle={subtitle}
      {...props}
    >
      <div className="grid w-max md:grid-cols-2 gap-x-10 gap-y-4">
        <Field
          data-testid={`${stepName}-referenceName`}
          label={content.referenceName.label}
          validationError={error.referenceName}
        >
          <Input
            id={`${stepName}-referenceName`}
            name={'referenceName'}
            defaultValue={values.referenceName}
            onChange={handleInputOnChange}
            aria-invalid={!!error.referenceName}
            onBlur={handleInputOnBlur}
            placeholder={content.referenceName.placeholder}
          />
        </Field>

        <Field
          data-testid={`${stepName}-referenceEmail`}
          label={content.referenceEmail.label}
          validationError={error.referenceEmail}
          warning={warning.referenceEmail}
        >
          <Input
            id={`${stepName}-referenceEmail`}
            name={'referenceEmail'}
            defaultValue={values.referenceEmail}
            onChange={handleInputOnChange}
            aria-invalid={!!error.referenceEmail}
            onBlur={handleInputOnBlur}
            placeholder={content.referenceEmail.placeholder}
            type="email"
          />
        </Field>
      </div>
    </QuestionOrganism>
  );
};

export default CompanyReference;
